import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AuthContext from 'context/AuthContext';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import classes from 'styles/globalStyle.module.scss';
import { UrlHelpers } from 'urlHelpers/urlHelper';

import AlertDetailsModal from './AlertDetailsModal';
import NewInvestigation from './NewInvestigationModal';

const AlertDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { accessToken } = useContext(AuthContext);

  const [showNewInvestigation, setShowNewInvestigation] = useState(false);

  const fetchAlerts = async (statusValue = 'All', searchValue = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusValue !== 'All') {
        params.append('lifecycleState', statusValue);
      }
      if (searchValue.trim()) {
        params.append('q', searchValue.trim());
      }
      const url = params.toString()
        ? `${UrlHelpers.getAutopsAlert}?${params.toString()}`
        : `${UrlHelpers.getAutopsAlert}`;
      const response = await fetch(url, {
        headers: accessToken
          ? { Authorization: `Bearer ${accessToken}`, appid: 'notifyadmin' }
          : {},
      });
      const data = await response.json();
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  const totalAlerts = alerts.length;
  const pendingCount = alerts.filter((a) => a.lifecycleState === 'Pending').length;
  const analyzedCount = alerts.filter((a) => a.lifecycleState === 'Analyzed').length;
  const reviewedCount = alerts.filter((a) => a.lifecycleState === 'Reviewed').length;
  const rejectedCount = alerts.filter((a) => a.lifecycleState === 'Rejected').length;
  const needsAttentionCount = alerts.filter((a) => a.needsAttention).length;

  const formatActivityDateTime = (value) => {
    if (!value) return '-';

    const normalized =
      typeof value === 'string' ? value.replace(/(\d{2}:\d{2}:\d{2}):(\d+)/, '$1.$2') : value;

    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const getSeverityStyles = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'HIGH':
        return {
          bgcolor: '#ffebee',
          color: '#dc2626',
        };

      case 'MEDIUM':
        return {
          bgcolor: '#fff3e0',
          color: '#d97706',
        };

      case 'LOW':
        return {
          bgcolor: '#f8fafc',
          color: '#6b7280',
          border: '1px solid #d1d5db',
        };

      default:
        return {
          bgcolor: '#f5f5f5',
          color: '#000',
        };
    }
  };

  if (showNewInvestigation) {
    return (
      <NewInvestigation
        onBack={() => setShowNewInvestigation(false)}
        refreshAlerts={() => fetchAlerts(status, searchInput)}
      />
    );
  }

  return (
    <Box>
      <Typography
        variant="h3"
        component="h3"
        my={2}
        className={classes.heading}
        sx={{ display: 'flex' }}
      >
        AutOps
        <Box>
          <Chip
            label="Incident Response"
            size="small"
            sx={{
              ml: 2,
              bgcolor: '#ede9fe',
              color: '#7c3aed',
            }}
          />
        </Box>
      </Typography>

      <Box>
        <Typography
          sx={{
            fontWeight: 600,
            mb: 1,
            fontSize: '24px !important',
          }}
        >
          Alert Resolution Dashboard
        </Typography>

        <Typography sx={{ mb: 4, color: '#6b7280', fontSize: '15px !important' }}>
          Select an alert to start a read-only investigation.
        </Typography>

        {loading ? (
          <Typography variant="h6">Loading alerts...</Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
                mb: '20px',
              }}
            >
              <SummaryCard count={totalAlerts} label="TOTAL ALERTS" />
              <SummaryCard count={pendingCount} label="PENDING" />
              <SummaryCard count={needsAttentionCount} label="NEEDS ATTENTION" />
              <SummaryCard count={analyzedCount} label="ANALYZED" />
              <SummaryCard count={reviewedCount} label="REVIEWED" />
              <SummaryCard count={rejectedCount} label="REJECTED" />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 3,
                gap: '20px',
              }}
            >
              <Box display="flex" gap={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    STATUS
                  </Typography>

                  <FormControl size="small">
                    <Select
                      value={status}
                      onChange={(e) => {
                        const { value } = e.target;

                        setStatus(value);
                        fetchAlerts(value, searchInput);
                      }}
                      sx={{ width: 120 }}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Analyzed">Analyzed</MenuItem>
                      <MenuItem value="Reviewed">Reviewed</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    Search
                  </Typography>

                  <TextField
                    size="small"
                    placeholder="Search by alarm name..."
                    value={searchInput}
                    onChange={(e) => {
                      const { value } = e.target;
                      setSearchInput(value);
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  sx={{ alignSelf: 'end' }}
                  onClick={() => {
                    fetchAlerts(status, searchInput);
                  }}
                >
                  Search
                </Button>
              </Box>

              <Typography
                sx={{
                  fontWeight: 600,
                  alignSelf: 'center',
                  cursor: 'pointer',
                  fontSize: '13px !important',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => setShowNewInvestigation(true)}
              >
                + New investigation (submit an error not covered by these alerts)
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                overflowX: 'auto',
                border: '1px solid rgb(242, 242, 242)',
              }}
            >
              <Paper sx={{ width: '100%', boxShadow: 'none' }}>
                <Table aria-label="autops-table">
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: '#f2f2f2' }}
                      className={classes.tableHeadingApplications}
                    >
                      <TableCell>ALERT ID</TableCell>
                      <TableCell>INCIDENT TYPE</TableCell>
                      <TableCell>TITLE</TableCell>
                      <TableCell>SEVERITY</TableCell>
                      <TableCell>SOURCE</TableCell>
                      <TableCell>TIMESTAMP</TableCell>
                      <TableCell>STATUS</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {alerts.map((alert) => (
                      <TableRow
                        key={alert.alertId}
                        className={classes.tableHeadingApplications}
                        sx={{
                          cursor: 'pointer',
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                        onClick={() => {
                          setSelectedId(alert.alertId);
                          setOpenModal(true);
                        }}
                      >
                        <TableCell>{alert.alertId?.slice(0, 8)}</TableCell>

                        <TableCell>
                          <Chip size="small" label={alert.errorType || 'TECHNICAL'} />
                        </TableCell>

                        <TableCell>{alert.alarmName}</TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={alert.criticality}
                            sx={{
                              ...getSeverityStyles(alert.criticality),
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>

                        <TableCell>{alert.application}</TableCell>

                        <TableCell>{formatActivityDateTime(alert.activityDatetime)}</TableCell>

                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Chip size="small" label={alert.lifecycleState} color="success" />

                            {alert.needsAttention && (
                              <Chip
                                size="small"
                                label="Needs Attention"
                                sx={{
                                  bgcolor: '#fff3e0',
                                  color: '#f57c00',
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
            <AlertDetailsModal
              open={openModal}
              alertId={selectedId}
              onClose={() => setOpenModal(false)}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

const SummaryCard = ({ count, label }) => (
  <Box
    sx={{
      padding: '14px 16px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
    }}
  >
    <Typography
      sx={{
        fontSize: '24px !important',
        fontWeight: 700,
      }}
    >
      {count}
    </Typography>

    <Typography
      sx={{
        fontSize: '12px !important',
        color: '#666',
      }}
    >
      {label}
    </Typography>
  </Box>
);

SummaryCard.propTypes = {
  count: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

export default AlertDashboard;
